import { usePathname } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useQuinceRouter } from '@src/modules/navigation' // This is intentional for making the progress bar work for subcollection
import {
  SearchResultsSectionConfiguratorContextType,
  SearchResultsSectionConfiguratorProvider,
} from '@src/pages/search/searchResultsSection/SearchResultsSectionConfigurator'
import { LoaderWrapper } from '@src/quince-components/src'
import { BreadcrumbSchema } from '@src/quince-components/src/atoms/breadCrumb'
import { getDirectory } from '@src/quince-components/src/atoms/breadCrumb/breadcrumb.helper'
import { If } from '@src/quince-components/src/atoms/conditionals'
import { MAX_FILTERS, SORT_BY } from '@src/quince-components/src/atoms/filterSection/constants/constants'
import {
  filterListOnExp,
  useShouldHideDeptAndCategoryFilter,
} from '@src/quince-components/src/atoms/filterSection/filterListOnExp'
import { FilterWithSidebarItemCountAndSort } from '@src/quince-components/src/atoms/filterSection/menu'
import { excludeDepartmentFilter } from '@src/quince-components/src/atoms/filterSection/menu/filterUtils'
import { PRODUCT_SORT_VALUES } from '@src/quince-site-common/src/constants/product'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useCaptureDataException from '@src/quince-site-common/src/hooks/useCaptureDataException'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import useMounted from '@src/quince-site-common/src/hooks/useMounted'
import { useAllQueryState } from '@src/quince-site-common/src/hooks/useQueryState'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import SomethingWentWrong from '@src/quince-site-common/src/organisms/somethingWentWrong/somethingWentWrong'
import collectionCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/CollectionCacheStateManager'
import filterFalsy from '@src/quince-site-common/src/utils/filterFalsy'
import { getCurrentPageGenderContext } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { GetSeoMetapageDataProps } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { cloneDeep, pickBy } from '@src/utils/functions/utils'
import { getDisplayKey, parseLocale } from '@src/utils/i18n.utils'

import { useCollectionFeature } from './collection-feature-provider'
import { ALL_SUBCOLLECTION_FILTER_ID, COLLECTION_TYPES, trackingMetaData } from './collection.constants'
import {
  getNavigateTo,
  getProductTypeWithPageForStaticCollections,
  getQueryParamsForSelectedFilters,
  getTrackerUrlForSelectedFilters,
  getVariantsListFromCollectionNew,
  isFeedDynamic,
  modifyFilteredCollectionOnProductFeed,
  splitCollectionSubSections,
} from './collection.helpers'
import styles from './collectionPage.module.scss'
import Collection, { COLLECTION_PAGE_TYPE, SubCollection } from './domains/Collection'
import { EFilterKeys, Filter, SelectedFilter, UpdateQueryParams } from './domains/CollectionFilter'
import { useStatsigNewCLPFilterExperiment } from './hooks/useStatsigNewCLPFilterExperiment'
import CollectionFilters from './molecules/collectionFilters/CollectionFilters'
import { CollectionTitle } from './molecules/collectionTitle'
import { CustomSectionLazyLoader } from './molecules/custom-section-lazy-loader/custom-section-lazy-loader.component'
import { CustomCollectionPageSection } from './molecules/customCollectionPageSection/CustomCollectionPageSection'
import NoProduct from './molecules/noProducts'
import { SubCategoryList } from './molecules/subCategoryList'
import { SubCollectionCardList } from './molecules/subCollectionCardList'
import SubCollectionSection from './molecules/subCollectionSection'
import collectionService from './services/collectionService/CollectionService'
import { ECollectionType } from './services/collectionService/CollectionService.types'
import useCollectionCache from './useCollectionCache'
import {
  getFormattedSlug,
  getShowSecondImageOnProductTilesValue,
  isUndefined,
  shouldShowBorder,
  shouldShowSubCollectionCards,
} from './utils'

// @i18n-ignore
const QUERY_KEYS: ('filter' | 'sortBy')[] = ['filter', 'sortBy']

export function CollectionPage({
  collectionPage,
  pathname: serverPathname,
  searchParams: serverSearchParams,
  seoData,
  alternateUrls,
}: Readonly<{
  collectionPage: Collection
  pathname: string
  searchParams: string
  seoData: GetSeoMetapageDataProps | null
  alternateUrls?: Record<string, string>
}>) {
  // Please make sure to put the state which is needed in the component to make collection page cache work
  // We are not using useAppSelector here because we are not using react state management for the cache
  // We are storing only JSON of the collection page to feed the props/state/refs of the component
  const { pageCache, hasPaginationTerminatedCache, currentCacheKey, scrollY, nextFeedURLCache } = useCollectionCache({
    pathname: serverPathname,
    searchParams: serverSearchParams,
  })

  const isStaticPage = collectionPage.collectionType === (ECollectionType.STATIC as string)

  const baseSortBy = useMemo(() => {
    // return isStaticPage ? PRODUCT_SORT_VALUES.CLP_SORT_V2 : PRODUCT_SORT_VALUES.FEATURED
    return PRODUCT_SORT_VALUES.FEATURED
    // eslint-disable-next-line
  }, [isStaticPage])

  const isI18nPreviewEnabled = useI18nPreviewContext()
  const { locale } = useLocaleContext()
  const { country } = parseLocale(locale)
  const isMounted = useMounted()
  const router = useQuinceRouter()
  const pathname = usePathname()
  const query = router.query
  const hasPaginationTerminated = useRef<boolean>(hasPaginationTerminatedCache ?? false)
  const [watchedQueryStates, setWatchedQueryStates] = useAllQueryState<'filter' | 'sortBy'>(QUERY_KEYS)
  const { selectedFilters, sortBy } = useMemo(() => {
    const selectedFilters = watchedQueryStates.filter
      ? SelectedFilter.getSelectedFilterFromQueryParams(watchedQueryStates.filter)
      : new SelectedFilter({})
    const sortBy = watchedQueryStates.sortBy ?? SORT_BY.FEATURED.value

    return { selectedFilters, sortBy }
  }, [watchedQueryStates])

  const productCardsSectionRef = useRef<HTMLDivElement>(null)

  const isCollectionViewEventTracked = useRef(false)

  const productViewTracker = `${collectionPage.slug}_${TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW}`

  useBatchedAnalyticsForProductCard({
    productViewTracker,
    trackingMetaData,
  })

  const {
    isNewCLPFiltersExperimentActive,
    showDesktopFilterWithSidebar,
    showMobileFilterWithDrawer,
    isNewCLPFiltersDesktopActive,
    newCLPFiltersDesktopBucket,
    newCLPFiltersMobileBucket,
    collectionPageType,
    isNewCLPFiltersExperimentInitialized,
    showDesktopFilterWithDrawer,
    showMixedPage,
    showGroupedPage,
  } = useStatsigNewCLPFilterExperiment()

  const collectionFeature = useCollectionFeature()

  const searchResultsSectionConfiguration = useMemo(() => {
    const config: SearchResultsSectionConfiguratorContextType = {
      isNewCLPFiltersExperimentActive,
      showDesktopFilterWithSidebar,
      showMobileFilterWithDrawer,
      isNewCLPFiltersExperimentInitialized,
      showDesktopFilterWithDrawer,
    }
    return config
  }, [
    isNewCLPFiltersExperimentActive,
    showDesktopFilterWithSidebar,
    showMobileFilterWithDrawer,
    showDesktopFilterWithDrawer,
    isNewCLPFiltersExperimentInitialized,
  ])

  const isClpPersonalized = useMemo(() => {
    switch (collectionPage.collectionType as COLLECTION_TYPES) {
      case COLLECTION_TYPES.ROOT:
        return true
      case COLLECTION_TYPES.BESTSELLER:
        return collectionFeature.bestSellerExperimentBucket === 'show'
      default:
        return false
    }
  }, [collectionFeature, collectionPage.collectionType])

  const showSecondImageOnProductTiles = getShowSecondImageOnProductTilesValue(collectionPage.slug)

  const initialSubCollections = collectionPage.subCollections

  const [filteredSubCollections, setFilteredSubCollections] =
    useState<(SubCollection | CustomSection)[]>(initialSubCollections)

  const [initialFilters, setInitialFilters] = useState<Filter[]>(collectionPage.filters)
  const [nextFeedURL, setNextFeedURL] = useState<string>(
    !isUndefined(pageCache) ? (nextFeedURLCache ?? '') : (collectionPage.nextFeedURL ?? ''),
  )

  const [showSubCollectionsPageLoader, setShowSubCollectionsPageLoader] = useState<boolean>(
    !isUndefined(pageCache) ? false : isFeedDynamic(collectionPage.slug),
  )

  const isJumpLink = query.jumplink === 'true'

  const [showSomethingWentWrong, setShowSomethingWentWrong] = useState(false)
  const { zipCode } = useZipCode()

  const showSubCollectionCards = shouldShowSubCollectionCards(collectionPage.subCollectionCards)

  const subCollectionTitle = useMemo(() => {
    if (!isNewCLPFiltersExperimentActive) {
      return ''
    }

    // For mixed pages, use selectedFilters
    if (selectedFilters.subcollections?.length === 1) {
      return selectedFilters.subcollections[0]
    }

    // For grouped pages, derive title from current pathname
    if (showGroupedPage) {
      const formattedPathname = getFormattedSlug(pathname)
      const matchingSubCollection = collectionPage.subCollectionCards.find(subCollection => {
        const formattedSubCollectionSlug = subCollection.slug ? getFormattedSlug(subCollection.slug) : ''
        return formattedPathname === formattedSubCollectionSlug
      })
      return matchingSubCollection?.title ?? ''
    }

    return ''
  }, [
    isNewCLPFiltersExperimentActive,
    selectedFilters.subcollections,
    showGroupedPage,
    pathname,
    collectionPage.subCollectionCards,
  ])

  const filteredList = filteredSubCollections.filter(
    sc => sc.typename === 'CustomSection' || (sc.productCards?.length ?? -1) > 0,
  )

  const isFilteredSubCollectionsEmpty = !filteredSubCollections.length
  useCaptureDataException({
    captureException: isFilteredSubCollectionsEmpty,
    errorMsg: `No section is present for collection page: ${collectionPage.slug}`,
    // @i18n-ignore
    name: 'Collection page section check',
    module: MODULES.COLLECTION_FILTER,
    feature: FEATURES.FILTER_CHANGE,
  })

  const totalProductsCurrentlyOnPage = filteredList
    .filter(subcollection => subcollection.typename === 'SubCollection')
    .map(subcollection => subcollection.productCards)
    .flat().length

  const [totalProductsCount, setTotalProductsCount] = useState<number>(collectionPage.totalCount ?? 0)

  const isProductAbsent = totalProductsCurrentlyOnPage === 0

  useCaptureDataException({
    captureException: isProductAbsent,
    errorMsg: `No products on collection page: ${collectionPage.slug}`,
    // @i18n-ignore
    name: 'Collection page number of products check',
    module: MODULES.COLLECTION_FILTER,
    feature: FEATURES.FILTER_CHANGE,
  })

  const filterSectionRef = useRef<HTMLDivElement>(null)
  const productSectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isMounted || getFormattedSlug(collectionPage.slug) !== getFormattedSlug(pathname)) {
      trackEvent(TELEMETRY_EVENT_NAMES.COLLECTION_VIEW, {
        collectionSlug: getFormattedSlug(collectionPage.slug),
        collectionTitle: collectionPage.title,
        variantList: getVariantsListFromCollectionNew(filteredList),
        filterList: initialFilters,
        zipCode: zipCode,
        isDynamicCollectionPage: isFeedDynamic(collectionPage.slug),
      })
      setFindingMethod[FINDING_METHOD_AGGREGATE.LANDING_PAGE]?.()
      isCollectionViewEventTracked.current = true
    }
    // TODO: This is just for events, and we want them to fired on only pathname and searchParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, query])

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  // For infinite Feed.
  const [intersectRef, isIntersecting] = useIntersect(null, isDesktop ? '3500px' : '2000px', 0)
  const [showNextFeedLoading, setShowNextFeedLoading] = useState(false)
  const [page, setPage] = useState(pageCache ?? 0)

  useEffect(() => {
    if (isIntersecting && !showSomethingWentWrong && !hasPaginationTerminated.current && !showNextFeedLoading) {
      if (totalProductsCount <= totalProductsCurrentlyOnPage) return

      if (totalProductsCurrentlyOnPage === 0 && page != 0) return

      if (!nextFeedURL && (['pickedForYou', 'lastChance'].includes(collectionPage.collectionType) || isClpPersonalized))
        return

      let newPage = page + 1
      let productType
      if (
        ['static', 'allNewArrivals'].includes(collectionPage.collectionType) &&
        (!isNewCLPFiltersExperimentActive || showGroupedPage)
      ) {
        const {
          productType: newProductType,
          page: newPageStatic = 0,
          doNotFetch,
        } = getProductTypeWithPageForStaticCollections(
          filteredSubCollections.filter(sc => sc.typename === 'SubCollection') as SubCollection[],
          newPage,
        )

        if (doNotFetch) return

        newPage = newPageStatic
        productType = newProductType
      } else {
        const doNotFetch = (
          filteredSubCollections.filter(sc => sc.typename === 'SubCollection') as SubCollection[]
        ).every(sc => {
          if (sc.productCards === undefined) return true

          if (sc.productCards.length === 0) return false

          if (sc.totalProducts === sc.productCards.length) {
            return true
          }
          return false
        })

        if (doNotFetch) return
      }

      setShowNextFeedLoading(true)
      setShowSomethingWentWrong(false)

      const queryParams = getQueryParamsForSelectedFilters(selectedFilters, sortBy, collectionPage.slug)
      collectionService
        .getNextPageCollection({
          slug: collectionPage.slug,
          nextFeedURL,
          filter: queryParams.filter,
          sortBy: (queryParams.sortBy as PRODUCT_SORT_VALUES | undefined) ?? baseSortBy,
          baseSortBy,
          collectionType: collectionPage.collectionType,
          currentSlug: pathname,
          page: newPage,
          productType,
          pageType: collectionPageType,
          isClpPersonalized,
          collectionId: collectionPage.contentful_id,
          isI18nPreviewEnabled,
          country,
        })
        .then(resp => {
          setPage(newPage)
          if (
            (['newArrivals', 'root', 'bestseller'].includes(collectionPage.collectionType) ||
              (isNewCLPFiltersExperimentActive && !showGroupedPage)) &&
            !resp.subCollections.length
          ) {
            hasPaginationTerminated.current = true
          }
          const newFilteredSubCollections = cloneDeep(filteredSubCollections)

          modifyFilteredCollectionOnProductFeed(
            resp,
            newFilteredSubCollections,
            newPage,
            false,
            collectionPage,
            (isNewCLPFiltersExperimentActive && !showGroupedPage) || isClpPersonalized,
          )

          // Handle grouped page filtering and total count updates
          const total = resp.total
          if (typeof total === 'number' && !isNaN(total)) {
            setTotalProductsCount(total)
          }

          setShowNextFeedLoading(false)
          setNextFeedURL(resp.next_url)
          setFilteredSubCollections(newFilteredSubCollections)
        })
        .catch((e: unknown) => {
          setShowNextFeedLoading(false)
          setShowSomethingWentWrong(true)
          captureException(e, {
            module: MODULES.COLLECTION_PRODUCT_LOAD,
            feature: FEATURES.PAGINATION,
          })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isIntersecting,
    collectionPage.slug,
    page,
    filteredSubCollections,
    showSomethingWentWrong,
    totalProductsCurrentlyOnPage,
    collectionPage.collectionType,
    totalProductsCount,
    pathname,
    nextFeedURL,
    selectedFilters,
    sortBy,
    baseSortBy,
    collectionPage,
    isNewCLPFiltersExperimentActive,
    showNextFeedLoading,
    isClpPersonalized,
    collectionPageType,
    showGroupedPage,
  ])

  useEffect(() => {
    if (showSubCollectionsPageLoader && isFeedDynamic(collectionPage.slug)) {
      // Initial API Call
      const newPage = 0
      const queryParams = getQueryParamsForSelectedFilters(selectedFilters, sortBy, collectionPage.slug)
      collectionService
        .getNextPageCollection({
          slug: collectionPage.slug,
          nextFeedURL: '',
          page: newPage,
          filter: queryParams.filter,
          sortBy: (queryParams.sortBy as PRODUCT_SORT_VALUES | undefined) ?? baseSortBy,
          baseSortBy,
          collectionType: collectionPage.collectionType,
          currentSlug: pathname,
          pageType: collectionPageType,
          isClpPersonalized,
          collectionId: collectionPage.contentful_id,
          isI18nPreviewEnabled,
          country,
        })
        .then(resp => {
          const newFilteredSubCollections = cloneDeep(filteredSubCollections)
          modifyFilteredCollectionOnProductFeed(
            resp,
            newFilteredSubCollections,
            newPage,
            true,
            collectionPage,
            isNewCLPFiltersExperimentActive,
          )
          const total = resp.total
          if (typeof total === 'number' && !isNaN(total)) {
            setTotalProductsCount(total)
          }
          setFilteredSubCollections(newFilteredSubCollections)
          if (resp.next_url) {
            setNextFeedURL(resp.next_url)
          }

          // Update total count from API response for non-grouped pages
          if (!showGroupedPage && !isNaN(resp.total)) {
            setTotalProductsCount(resp.total)
          }

          if (isFeedDynamic(collectionPage.slug)) setInitialFilters(resp.filters)

          setShowSubCollectionsPageLoader(false)
        })
        .catch((e: unknown) => {
          setShowSomethingWentWrong(true)
          setShowSubCollectionsPageLoader(false)
          captureException(e, {
            module: MODULES.COLLECTION_PRODUCT_LOAD,
            feature: FEATURES.PAGINATION,
          })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showSubCollectionsPageLoader,
    collectionPage.slug,
    selectedFilters,
    sortBy,
    collectionPage.collectionType,
    baseSortBy,
    isClpPersonalized,
    collectionPageType,
    collectionPage.contentful_id,
    collectionPage,
    filteredSubCollections,
    isNewCLPFiltersExperimentActive,
    pathname,
    showGroupedPage,
  ])

  useEffect(() => {
    if (isUndefined(pageCache)) {
      setShowSubCollectionsPageLoader(isFeedDynamic(collectionPage.slug))

      if (page !== 0) {
        setPage(0)
      }
    }

    return () => {
      if (isUndefined(pageCache)) {
        hasPaginationTerminated.current = false
      }
    }
    // this should not be run on page change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, query, pageCache, collectionPage.slug])

  useEffect(() => {
    setFilteredSubCollections(collectionPage.subCollections)
  }, [collectionPage.subCollections])

  useEffect(() => {
    setNextFeedURL(collectionPage.nextFeedURL ?? '')
  }, [collectionPage.nextFeedURL])

  useEffect(() => {
    setTotalProductsCount(collectionPage.totalCount ?? 0)
  }, [collectionPage.totalCount])

  useEffect(() => {
    if (isUndefined(pageCache) && isJumpLink && filterSectionRef.current && productSectionRef.current) {
      if (isNewCLPFiltersExperimentActive && !isDesktop && (showGroupedPage || showMixedPage)) {
        window.scroll({
          top: 0,
          // @i18n-ignore
          behavior: 'smooth',
        })
        return
      }
      const filterSectionHeight = filterSectionRef.current.clientHeight
      const productSectionTop = productSectionRef.current.getBoundingClientRect().top
      const scrollOffset = filterSectionHeight
      const topOfElement = productSectionTop - scrollOffset
      window.scroll({
        top: topOfElement,
        // @i18n-ignore
        behavior: 'smooth',
      })
    }
  }, [isJumpLink, pageCache, isDesktop, isNewCLPFiltersExperimentActive, showGroupedPage, showMixedPage])

  useEffect(() => {
    // Update the cache with the latest data after the component has done rendering
    queueMicrotask(() => {
      collectionCacheStateManager.setCollectionCache(
        {
          collectionPage: {
            ...collectionPage,
            subCollections: filteredSubCollections,
            nextFeedURL,
            filters: initialFilters,
            totalCount: totalProductsCount,
          },
          page,
          hasPaginationTerminated: hasPaginationTerminated.current,
          scrollY: scrollY.current,
        },
        currentCacheKey.current,
      )
    })
  }, [
    filteredSubCollections,
    nextFeedURL,
    initialFilters,
    totalProductsCount,
    page,
    serverPathname,
    serverSearchParams,
    collectionPage,
    scrollY,
    currentCacheKey,
  ])

  // Single useEffect to handle initialFilters updates for both cached and non-cached scenarios
  useEffect(() => {
    setInitialFilters(collectionPage.filters)
  }, [collectionPage.filters])

  //Prevent clicking on already selected subcollection to maintain consistent behavior with production.
  const naviagteToSelectedSubcategoryPage = (_slug: string | undefined, addJumplink = true, clearCache = false) => {
    if (_slug) {
      const slug = getFormattedSlug(_slug)
      const currentPath = window.location.pathname // Get the current pathname

      // Early return if we're already on the target path
      if (currentPath === `/${slug}`) {
        return
      }

      let newPath = `/${slug}`
      const queryParams = new URLSearchParams()

      if (addJumplink) {
        queryParams.set('jumplink', 'true')
      }

      const queryString = queryParams.toString()
      if (queryString) {
        newPath += `?${queryString}`
      }

      if (clearCache && !isDesktop) {
        collectionCacheStateManager.deleteEntriesThatStartsWith(slug)
      }

      router.push(newPath)
    }
  }

  const updateQuery = useCallback(
    ({ filter, sort }: UpdateQueryParams, options?: { skipFilterClickEvent?: boolean }) => {
      let updatedSelectedFilters = selectedFilters
      let updatedSortBy = sortBy
      if (filter) {
        switch (filter.type) {
          case 'remove': {
            updatedSelectedFilters = SelectedFilter.removeValueFromSelectedFilter(
              selectedFilters,
              filter.value,
              filter.name,
            )
            trackEvent(TELEMETRY_EVENT_NAMES.INDIVIDUAL_FILTERS_CLEARED, {
              filter_key: filter.name,
              filter_value: filter.value,
            })
            break
          }
          case 'toggle': {
            if (filter.name !== 'productType') {
              updatedSelectedFilters = SelectedFilter.toggleValueFromSelectedFilter(
                selectedFilters,
                filter.name,
                filter.value,
              )
            }
            if (filter.name === 'productType' && isNewCLPFiltersExperimentActive) {
              updatedSelectedFilters = SelectedFilter.toggleValueFromSelectedFilter(
                selectedFilters,
                EFilterKeys.SUBCOLLECTIONS,
                filter.value,
              )
            }
            if (!options?.skipFilterClickEvent) {
              trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
                label: filter.label,
                value: filter.value,
                context: {
                  collectionSlug: collectionPage.slug,
                  collectionTitle: collectionPage.title,
                  isSelected: SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value),
                  selectedFilters: pickBy(updatedSelectedFilters as Record<string, unknown>, v => v !== undefined),
                  filter_key: filter.name,
                  filter_value: filter.value,
                },
              })
              if (
                !isNewCLPFiltersExperimentActive &&
                SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value)
              ) {
                trackEvent(TELEMETRY_EVENT_NAMES.FILTER_APPLIED, {
                  filter_key: filter.name,
                  filter_value: filter.value,
                })
              }
            }
            // ISSUE: @tech-debt productType shouldn't be a filter and isn't a filter - forced custom logic shouldn't be added
            if (filter.name === 'productType' && !isNewCLPFiltersExperimentActive)
              router.push(`/${filter.value}?jumplink=true`)
            break
          }
          case 'reset': {
            updatedSelectedFilters = new SelectedFilter({})
            trackEvent(TELEMETRY_EVENT_NAMES.FILTERS_CLEARED, {})
            break
          }

          case 'overwrite': {
            updatedSelectedFilters = filter.updatedFilters
            break
          }

          case 'removeAll': {
            updatedSelectedFilters = SelectedFilter.removeAllValuesFromSelectedFilter(selectedFilters, filter.name)
            if (!options?.skipFilterClickEvent) {
              trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
                label: filter.label,
                value: filter.value,
                context: {
                  collectionSlug: collectionPage.slug,
                  collectionTitle: collectionPage.title,
                  isSelected: SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value),
                  selectedFilters: pickBy(updatedSelectedFilters as Record<string, unknown>, v => v !== undefined),
                  filter_key: filter.name,
                  filter_value: filter.value,
                },
              })
            }
            break
          }
          default:
            break
        }
      }
      if (sort) {
        switch (sort.type) {
          case 'toggle': {
            const sortValue = sort.value
            updatedSortBy = sortValue === sortBy ? SORT_BY.FEATURED.value : sortValue
            break
          }
          case 'reset': {
            updatedSortBy = SORT_BY.FEATURED.value
            break
          }
          default:
            break
        }
        const isDefaultOption = updatedSortBy === SORT_BY.FEATURED.value
        if (!isDefaultOption) {
          trackEvent(TELEMETRY_EVENT_NAMES.SORT_USED, {
            sort_used: updatedSortBy,
          })
        }
      }
      if (updatedSortBy === sortBy && SelectedFilter.checkEquality(updatedSelectedFilters, selectedFilters)) return

      const updatedWatchedQueryState = {
        filter: SelectedFilter.getUpdatedSelectedFilterStringForWindowURL(updatedSelectedFilters),
        sortBy: updatedSortBy === SORT_BY.FEATURED.value ? undefined : updatedSortBy,
      }
      const navigateToOverwrite = getNavigateTo(updatedSelectedFilters, pathname)
      setWatchedQueryStates(updatedWatchedQueryState, navigateToOverwrite, !isNewCLPFiltersDesktopActive)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedFilters,
      sortBy,
      collectionPage.slug,
      collectionPage.title,
      router,
      pathname,
      setWatchedQueryStates,
      isNewCLPFiltersDesktopActive,
      isNewCLPFiltersExperimentActive,
      showMixedPage,
    ],
  )

  const applySubCollectionFilters = useCallback(
    (subCollection: SubCollection) => {
      const isSubCollectionFilterSelected = SelectedFilter.isFilterValueSelected(
        selectedFilters,
        EFilterKeys.SUBCOLLECTIONS,
        subCollection.title as string,
      )

      const newSelectedFilters = isSubCollectionFilterSelected
        ? new SelectedFilter({})
        : SelectedFilter.initializeWithProductType(subCollection.title as string)

      updateQuery({
        filter: {
          type: 'overwrite',
          updatedFilters: newSelectedFilters,
        },
      })
    },
    [updateQuery, selectedFilters],
  )

  const onSubCollectionCardClick = (subCollection: SubCollection) => {
    trackEvent(TELEMETRY_EVENT_NAMES.SUBCOLLECTION_FILTER_USED, {
      filter_available: subCollectionsForCardList.map(item => item.title),
      filter_used: subCollection.title,
    })

    // Early return for non-experiment pages
    if (!isNewCLPFiltersExperimentActive) {
      naviagteToSelectedSubcategoryPage(subCollection.slug, true, true)
      return
    }

    // Handle "All" card
    if (subCollection.id === ALL_SUBCOLLECTION_FILTER_ID) {
      if (showGroupedPage) {
        naviagteToSelectedSubcategoryPage(collectionPage.slug, false, true)
      } else {
        if (!isDesktop) {
          const currentPageURL = getFormattedSlug(collectionPage.slug)
          collectionCacheStateManager.deleteEntriesThatStartsWith(currentPageURL)
        }
        updateQuery(
          {
            filter: {
              type: 'overwrite',
              updatedFilters: new SelectedFilter({}),
            },
          },
          { skipFilterClickEvent: true },
        )
      }
      return
    }

    // Handle regular subcollection cards
    if (showMixedPage) {
      // For mixed pages, clear cache for current page since we're adding query params
      if (!isDesktop) {
        const currentPageURL = getFormattedSlug(collectionPage.slug)
        collectionCacheStateManager.deleteEntriesThatStartsWith(currentPageURL)
      }
      applySubCollectionFilters(subCollection)
      return
    }

    if (showGroupedPage) {
      const isCurrentlySelected = isSubCollectionSelected(subCollection)
      const targetSlug = isCurrentlySelected ? collectionPage.slug : subCollection.slug
      const addJumplink = !isCurrentlySelected
      naviagteToSelectedSubcategoryPage(targetSlug, addJumplink, true)
      return
    }

    // Default case
    naviagteToSelectedSubcategoryPage(subCollection.slug, true, true)
  }

  const subCollectionsForCardList = collectionPage.subCollectionCards

  const directory = getDisplayKey(collectionPage, 'breadCrumbs', getDirectory(pathname), isI18nPreviewEnabled)
  const collectionGender = getCurrentPageGenderContext(collectionPage.slug, '')
  const isRootCollectionPage = directory.paths.length < 2
  const hideCollectionPageTitle = collectionPage.slug === '/collections/trending'

  const showMobileCollectionBanner = !isDesktop && collectionPage.collectionBannerMobile
  const showDesktopCollectionBanner = isDesktop && collectionPage.collectionBannerDesktop

  const { subCollections, customSections } = splitCollectionSubSections(filteredList)

  const isSubCollectionSelected = useCallback(
    (subCollection: SubCollection) => {
      if (isNewCLPFiltersExperimentActive && showMixedPage) {
        return SelectedFilter.isFilterValueSelected(
          selectedFilters,
          EFilterKeys.SUBCOLLECTIONS,
          subCollection.title as string,
        )
      }

      if (isNewCLPFiltersExperimentActive && showGroupedPage) {
        const formattedPathname = getFormattedSlug(pathname)
        const formattedSubCollectionSlug = subCollection.slug ? getFormattedSlug(subCollection.slug) : ''
        return formattedPathname === formattedSubCollectionSlug
      }
      return false
    },
    [isNewCLPFiltersExperimentActive, selectedFilters, showMixedPage, showGroupedPage, pathname],
  )

  // Determine if filters are empty using the same logic as filter components
  const hideDeptAndCategoryFilter = useShouldHideDeptAndCategoryFilter()
  const processedFilters = useMemo(() => {
    const filteredList = filterListOnExp(
      excludeDepartmentFilter(initialFilters, collectionPage.slug),
      hideDeptAndCategoryFilter,
    )
      .filter((item: Filter) => item.values.length > 1)
      .slice(0, MAX_FILTERS)

    return filteredList
  }, [initialFilters, hideDeptAndCategoryFilter, collectionPage.slug])

  const hasFilters = processedFilters.length > 0
  const shouldHideFilters = collectionPage.collectionPageType === COLLECTION_PAGE_TYPE.BUNDLE || !hasFilters

  const renderSubCollectionCardList = () => {
    return (
      <SubCollectionCardList
        subCollections={subCollectionsForCardList}
        isSubCollectionSelected={isNewCLPFiltersExperimentActive ? isSubCollectionSelected : undefined}
        onCardClick={onSubCollectionCardClick}
        slug={collectionPage.slug}
        collectionExploreEverythingImage={collectionPage.collectionExploreEverythingImage}
        contentful_id={collectionPage.contentful_id}
        isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
        showMobileFilterWithDrawer={showMobileFilterWithDrawer}
        isNewCLPFiltersDesktopActive={isNewCLPFiltersDesktopActive}
      />
    )
  }

  const renderCollectionTitle = () => (
    <CollectionTitle
      title={collectionPage.title}
      hideCollectionPageTitle={hideCollectionPageTitle}
      isRootCollectionPage={isRootCollectionPage}
      isJumpLink={isJumpLink}
      subCollectionTitle={
        isNewCLPFiltersExperimentActive ? subCollectionTitle : collectionPage.subCollections[0]?.title
      }
      seoData={seoData}
      isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
      alternateUrls={alternateUrls}
    />
  )

  const shouldShowCustomSections =
    customSections.length > 0 &&
    !showNextFeedLoading &&
    !showSubCollectionsPageLoader &&
    !showSomethingWentWrong &&
    !collectionPage.nextFeedURL

  // Memoize the filters component to prevent unnecessary re-renders
  return (
    <SearchResultsSectionConfiguratorProvider config={searchResultsSectionConfiguration}>
      <div>
        {showMobileCollectionBanner ? (
          <CustomCollectionPageSection
            filteredSubCollection={collectionPage.collectionBannerMobile}
            filteredSubCollections={[]}
            slug={collectionPage.slug}
            isMounted={isMounted}
          />
        ) : (
          <SubCategoryList
            linkGroup={{
              buttons: filterFalsy(
                isDesktop ? collectionPage.subcategoriesLinkDesktop : collectionPage.subcategoriesLinkMobile,
              ),
            }}
            directory={directory}
            titleRenderer={renderCollectionTitle}
            contentful_id={collectionPage.contentful_id}
            isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
            showMobileFilterWithDrawer={showMobileFilterWithDrawer}
          />
        )}

        {isNewCLPFiltersExperimentActive && showSubCollectionCards && renderSubCollectionCardList()}
        <div className={isDesktop && showDesktopFilterWithSidebar && !shouldHideFilters ? styles.gridLayout : ''}>
          <div
            id="filter-section"
            ref={filterSectionRef}
            className={` ${
              showDesktopFilterWithSidebar
                ? `${styles.filterSection} ${styles.filterSectionTransition} ${styles.groupB}`
                : ''
            }`}
          >
            <CollectionFilters
              totalProductsCount={totalProductsCount}
              initialFilters={initialFilters}
              sortBy={sortBy}
              selectedFilters={selectedFilters}
              updateQuery={updateQuery}
              collectionPage={collectionPage}
              newCLPFiltersDesktopBucket={newCLPFiltersDesktopBucket}
              newCLPFiltersMobileBucket={newCLPFiltersMobileBucket}
              showLoader={showSubCollectionsPageLoader && !showSomethingWentWrong}
              showGroupedPage={showGroupedPage}
              subCollectionCards={collectionPage.subCollectionCards}
            />
          </div>
          <div>
            {!isNewCLPFiltersExperimentActive && showSubCollectionCards && renderSubCollectionCardList()}
            <div ref={productSectionRef}>
              {showDesktopCollectionBanner ? (
                <CustomCollectionPageSection
                  filteredSubCollection={collectionPage.collectionBannerDesktop}
                  filteredSubCollections={[]}
                  slug={collectionPage.slug}
                  isMounted={isMounted}
                />
              ) : (
                <>{!isNewCLPFiltersExperimentActive && isDesktop && renderCollectionTitle()}</>
              )}
              {totalProductsCount > 0 ? (
                <div className={styles.subcollection_wrapper}>
                  {showDesktopFilterWithSidebar && isDesktop ? (
                    <FilterWithSidebarItemCountAndSort
                      noOfItems={totalProductsCount}
                      sortBy={sortBy}
                      updateQuery={updateQuery}
                      className={`${styles.newCLPFiltersItemCountAndSortWrapper} ${!hasFilters ? styles.noFilters : ''}`}
                    />
                  ) : null}
                  {subCollections.map((filteredSubCollection, index, array) => {
                    return (
                      <React.Fragment key={filteredSubCollection.id}>
                        <If condition={filteredSubCollection.typename !== 'CustomSection'}>
                          <SubCollectionSection
                            key={filteredSubCollection.title}
                            subCollectionData={filteredSubCollection}
                            showBorderBottom={shouldShowBorder(index, array)}
                            isFirstCollection={index === 0}
                            tracker={`collection_page__${
                              collectionPage.slug
                            }${getTrackerUrlForSelectedFilters(selectedFilters)}`}
                            additionalAttributes={{
                              ...collectionPage.additionalAttributes,
                              ...filteredSubCollection.additionalAttributes,
                            }}
                            doHolidayCutoffSorting={!collectionPage.nextFeedURL}
                            collectionGender={collectionGender}
                            collectionSlug={collectionPage.slug}
                            productViewTracker={productViewTracker}
                            showSecondImageOnProductTiles={showSecondImageOnProductTiles}
                            ref={productCardsSectionRef}
                            isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
                            showDesktopFilterWithSidebar={showDesktopFilterWithSidebar && !shouldHideFilters}
                            showShimmerOnProductCards={showSubCollectionsPageLoader && !showSomethingWentWrong}
                            showMixedPage={showMixedPage}
                          />
                        </If>
                      </React.Fragment>
                    )
                  })}
                  <div id="boundary_bottom" className={styles.boundary_bottom} ref={intersectRef} />
                  <LoaderWrapper
                    testid={TEST_LOCATORS.COLLECTION_INFINITE_LOADER}
                    showLoader={showNextFeedLoading && !showSomethingWentWrong}
                    // eslint-disable-next-line i18next/no-literal-string
                    minHeight={'40rem'}
                  >
                    <div />
                  </LoaderWrapper>
                </div>
              ) : (
                <NoProduct
                  onResetFilters={() => {
                    updateQuery({ filter: { type: 'reset' }, sort: { type: 'reset' } })
                  }}
                  isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
                  ref={productCardsSectionRef}
                />
              )}
            </div>
            <SomethingWentWrong
              show={showSomethingWentWrong}
              // eslint-disable-next-line i18next/no-literal-string
              minHeight={'20rem'}
            />
            <BreadcrumbSchema directory={directory} />
          </div>
        </div>
        {shouldShowCustomSections && (
          <div className={styles.customSectionsWrapper}>
            {customSections.map(filteredCustomSection => {
              return (
                <CustomSectionLazyLoader
                  key={`${collectionPage.slug}_${filteredCustomSection.id}`}
                  slug={collectionPage.slug}
                  isPageCache={!isUndefined(pageCache)}
                >
                  <CustomCollectionPageSection
                    filteredSubCollection={filteredCustomSection}
                    filteredSubCollections={filteredSubCollections}
                    slug={collectionPage.slug}
                    isMounted={isMounted}
                  />
                </CustomSectionLazyLoader>
              )
            })}
          </div>
        )}
      </div>
    </SearchResultsSectionConfiguratorProvider>
  )
}
